import { useState, useRef, useEffect } from 'react';
import isEqual from 'lodash/isEqual';

export function useFormHandler<T>(initialState: T, submitCallback: (data: Partial<T>) => Promise<void>, cancelCallback?: () => void) {
    const [formData, setFormData] = useState<T>(() => initialState);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const formRef = useRef<HTMLFormElement | null>(null);

    useEffect(() => {
        if ( initialState && Object.keys(initialState).length > 0 ) {
            setFormData(initialState);
        }
    }, [initialState]);

    const onFieldChange = (
      nameOrEvent: string | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      value?: any,
    ) => {
        if ( typeof nameOrEvent === 'string' ) {
            setFormData((prevFormData) => ({
                ...prevFormData,
                [nameOrEvent]: value,
            }));
        } else {
            const { name, value } = nameOrEvent.target;
            setFormData((prevFormData) => ({
                ...prevFormData,
                [name]: value,
            }));
        }
    };


    const getChangedFields = () => {
        const changedFields: Partial<T> = {};
        for (const key in formData) {
            if (!isEqual(formData[key], initialState[key])) {
                changedFields[key] = formData[key];
            }
        }
        return changedFields;
    };

    const onSave = async () => {
        setError(null);
        setIsSuccess(false);
        setIsLoading(true);

        // const changedFields = getChangedFields();

        try {
            await submitCallback(formData);
            setIsSuccess(true);
            setError(null);
        } catch (err) {
            setError("Failed to save changes.");
            setIsSuccess(false);
        } finally {
            setIsLoading(false);
        }
    };

    const formReset = () => {
        setFormData(initialState);
        setIsSuccess(false);
        setIsLoading(false);
        setError(null);
        cancelCallback && cancelCallback();
    }

    return {
        formData,
        formReset,
        isLoading,
        isSuccess,
        error,
        formRef,
        onFieldChange,
        onSave,
        initialState,
    };
}
